import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../../../store/store";
import type { TransitionCondition } from "@gpms/survey-form";
import { buildStepTranferForFlowCanvas } from "../process-managements/FormStepV2/parent/transferOrderHelpers";

/** Nguồn patch transition_conditions survey: canvas (flow) hoặc form. */
export type ProcessFlowSchemaTransitionSource = "flow" | "form";

export type ProcessFlowSchemaCachePatch = {
  schemaId: number;
  processStepId: number | null;
  jsonFormSchema: unknown;
  revision: number;
};

type ProcessFlowSyncState = {
  /** step_order → stepTransferCondition[] (topology canvas / form ĐKCT cổ) */
  transferConditions: Record<number, any[]>;
  /** schemaId → survey transition_conditions (đồng bộ ProcessFlow ↔ FormStepV2) */
  schemaTransitionConditions: Record<number, TransitionCondition[]>;
  /** Tăng mỗi lần patch — FormStepV2 chỉ apply khi source === 'flow' */
  schemaTransitionRevision: Record<number, number>;
  schemaTransitionSource: Record<number, ProcessFlowSchemaTransitionSource>;
  /** step_order → schema vừa load sau save Survey — ProcessFlowSection merge vào schemasCacheRef */
  schemaCachePatches: Record<number, ProcessFlowSchemaCachePatch>;
};

const initialState: ProcessFlowSyncState = {
  transferConditions: {},
  schemaTransitionConditions: {},
  schemaTransitionRevision: {},
  schemaTransitionSource: {},
  schemaCachePatches: {}
};

const processFlowSyncSlice = createSlice({
  name: "processFlowSync",
  initialState,
  reducers: {
    /** Khởi tạo topology ĐKCT từ processSteps khi load process detail. */
    initProcessFlowSyncFromSteps(
      state,
      action: PayloadAction<{ processSteps: any[] }>
    ) {
      const { processSteps } = action.payload;
      const next: Record<number, any[]> = {};
      processSteps.forEach((step: any) => {
        const key = step.step_order;
        const cond = step.stepTransferCondition;
        if (key != null && Array.isArray(cond)) {
          next[key] = buildStepTranferForFlowCanvas(cond);
        }
      });
      state.transferConditions = next;
    },
    /** Cập nhật stepTransferCondition một bước (FormStepV2 save tab). */
    patchProcessFlowStepTransfer(
      state,
      action: PayloadAction<{ stepOrder: number; conditions: any[] }>
    ) {
      const { stepOrder, conditions } = action.payload;
      if (stepOrder == null) return;
      const canvasConditions = buildStepTranferForFlowCanvas(conditions);
      const prev = state.transferConditions[stepOrder];
      if (
        prev != null &&
        JSON.stringify(prev) === JSON.stringify(canvasConditions)
      ) {
        return;
      }
      state.transferConditions[stepOrder] = canvasConditions.map((c: any) => ({
        ...c
      }));
    },
    /** Cập nhật survey transition_conditions theo schemaId (flow ↔ form). */
    patchProcessFlowSchemaTransition(
      state,
      action: PayloadAction<{
        schemaId: number;
        conditions: TransitionCondition[];
        source: ProcessFlowSchemaTransitionSource;
      }>
    ) {
      const { schemaId, conditions, source } = action.payload;
      if (schemaId == null) return;
      const prev = state.schemaTransitionConditions[schemaId];
      if (
        prev != null &&
        JSON.stringify(prev) === JSON.stringify(conditions) &&
        state.schemaTransitionSource[schemaId] === source
      ) {
        return;
      }
      state.schemaTransitionConditions[schemaId] =
        conditions as TransitionCondition[];
      state.schemaTransitionRevision[schemaId] =
        (state.schemaTransitionRevision[schemaId] ?? 0) + 1;
      state.schemaTransitionSource[schemaId] = source;
    },
    /** Cập nhật schemasCacheRef canvas sau save Survey (theo step_order). */
    patchProcessFlowSchemaCache(
      state,
      action: PayloadAction<{
        stepOrder: number;
        schemaId: number;
        processStepId: number | null;
        jsonFormSchema: unknown;
      }>
    ) {
      const { stepOrder, schemaId, processStepId, jsonFormSchema } =
        action.payload;
      if (stepOrder == null || schemaId == null) return;
      const prev = state.schemaCachePatches[stepOrder];
      const sameIdentity =
        prev?.schemaId === schemaId && prev?.processStepId === processStepId;
      const sameJson =
        sameIdentity &&
        JSON.stringify(prev?.jsonFormSchema) === JSON.stringify(jsonFormSchema);
      // Nội dung giống hệt → không bump revision (tránh ProcessFlowSection rebuild thừa).
      if (sameJson) return;
      state.schemaCachePatches[stepOrder] = {
        schemaId,
        processStepId,
        jsonFormSchema,
        revision: (prev?.revision ?? 0) + 1
      };
    },
    resetProcessFlowSync(state) {
      state.transferConditions = {};
      state.schemaTransitionConditions = {};
      state.schemaTransitionRevision = {};
      state.schemaTransitionSource = {};
      state.schemaCachePatches = {};
    }
  }
});

export default processFlowSyncSlice.reducer;

export const {
  initProcessFlowSyncFromSteps,
  patchProcessFlowStepTransfer,
  patchProcessFlowSchemaTransition,
  patchProcessFlowSchemaCache,
  resetProcessFlowSync
} = processFlowSyncSlice.actions;

export const selectProcessFlowSyncState = (state: RootState) =>
  state.processFlowSync;

/** Topology ĐKCT theo step_order — dùng bởi ProcessFlowSection.parseProcessToFlow */
export const selectProcessFlowStepTransfers = (state: RootState) =>
  state.processFlowSync.transferConditions;

export const selectProcessFlowSchemaTransitions = (state: RootState) =>
  state.processFlowSync.schemaTransitionConditions;

export const selectProcessFlowSchemaTransitionRevision = (
  state: RootState,
  schemaId: number | undefined
) =>
  schemaId != null
    ? state.processFlowSync.schemaTransitionRevision[schemaId] ?? 0
    : 0;

export const selectProcessFlowSchemaTransitionSource = (
  state: RootState,
  schemaId: number | undefined
): ProcessFlowSchemaTransitionSource | undefined =>
  schemaId != null
    ? state.processFlowSync.schemaTransitionSource[schemaId]
    : undefined;

/**
 * Signature rebuild canvas khi form patch survey transition_conditions (source: form).
 * ProcessFlowSection debounce effect theo giá trị này.
 */
export const selectProcessFlowSchemaCachePatches = (state: RootState) =>
  state.processFlowSync.schemaCachePatches;

/** Trigger ProcessFlowSection merge patch → schemasCacheRef. */
export const selectProcessFlowSchemaCachePatchSignature = (state: RootState) =>
  Object.entries(state.processFlowSync.schemaCachePatches)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(
      ([stepOrder, patch]) => `${stepOrder}:${patch.schemaId}:${patch.revision}`
    )
    .join("|");

export const selectProcessFlowFormReloadSignature = (state: RootState) => {
  const { schemaTransitionSource, schemaTransitionRevision } =
    state.processFlowSync;
  return Object.entries(schemaTransitionSource)
    .filter(([, source]) => source === "form")
    .map(
      ([schemaId]) =>
        `${schemaId}:${schemaTransitionRevision[Number(schemaId)] ?? 0}`
    )
    .sort()
    .join("|");
};
